import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { storefrontApiRequest, type ShopifyProduct } from "./shopify";

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  requestInsuranceInvoice: boolean;
  setRequestInsuranceInvoice: (v: boolean) => Promise<void>;
  addItem: (item: Omit<CartItem, "lineId">) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

const CART_QUERY = `query cart($id: ID!) { cart(id: $id) { id totalQuantity } }`;

const CART_CREATE = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }`;

const CART_ADD = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { id lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } } }
      userErrors { field message }
    }
  }`;

const CART_UPDATE = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id } userErrors { field message } }
  }`;

const CART_REMOVE = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id } userErrors { field message } }
  }`;

const CART_ATTRS = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) { cart { id } userErrors { field message } }
  }`;

const CART_NOTE = `
  mutation cartNoteUpdate($cartId: ID!, $note: String!) {
    cartNoteUpdate(cartId: $cartId, note: $note) { cart { id } userErrors { field message } }
  }`;

function formatCheckoutUrl(url: string) {
  try { const u = new URL(url); u.searchParams.set("channel", "online_store"); return u.toString(); }
  catch { return url; }
}

function isNotFound(errs: Array<{ message: string }> = []) {
  return errs.some((e) => /cart not found|does not exist/i.test(e.message));
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      requestInsuranceInvoice: false,

      addItem: async (item) => {
        const { cartId, items, clearCart } = get();
        set({ isLoading: true });
        try {
          const existing = items.find((i) => i.variantId === item.variantId);
          if (!cartId) {
            const data = await storefrontApiRequest(CART_CREATE, {
              input: { lines: [{ quantity: item.quantity, merchandiseId: item.variantId }] },
            });
            const errs = data?.data?.cartCreate?.userErrors ?? [];
            if (errs.length) { console.error(errs); return; }
            const cart = data?.data?.cartCreate?.cart;
            if (!cart?.checkoutUrl) return;
            const lineId = cart.lines.edges[0]?.node?.id ?? null;
            set({
              cartId: cart.id,
              checkoutUrl: formatCheckoutUrl(cart.checkoutUrl),
              items: [{ ...item, lineId }],
            });
          } else if (existing) {
            if (!existing.lineId) return;
            const q = existing.quantity + item.quantity;
            const data = await storefrontApiRequest(CART_UPDATE, {
              cartId, lines: [{ id: existing.lineId, quantity: q }],
            });
            const errs = data?.data?.cartLinesUpdate?.userErrors ?? [];
            if (isNotFound(errs)) { clearCart(); return; }
            set({ items: get().items.map((i) => i.variantId === item.variantId ? { ...i, quantity: q } : i) });
          } else {
            const data = await storefrontApiRequest(CART_ADD, {
              cartId, lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
            });
            const errs = data?.data?.cartLinesAdd?.userErrors ?? [];
            if (isNotFound(errs)) { clearCart(); return; }
            const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges ?? [];
            const newLine = lines.find((l: { node: { merchandise: { id: string } } }) => l.node.merchandise.id === item.variantId);
            set({ items: [...get().items, { ...item, lineId: newLine?.node?.id ?? null }] });
          }
        } catch (e) { console.error("addItem", e); }
        finally { set({ isLoading: false }); }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) return get().removeItem(variantId);
        const { cartId, items, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest(CART_UPDATE, {
            cartId, lines: [{ id: item.lineId, quantity }],
          });
          const errs = data?.data?.cartLinesUpdate?.userErrors ?? [];
          if (isNotFound(errs)) { clearCart(); return; }
          set({ items: get().items.map((i) => i.variantId === variantId ? { ...i, quantity } : i) });
        } finally { set({ isLoading: false }); }
      },

      removeItem: async (variantId) => {
        const { cartId, items, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest(CART_REMOVE, { cartId, lineIds: [item.lineId] });
          const errs = data?.data?.cartLinesRemove?.userErrors ?? [];
          if (isNotFound(errs)) { clearCart(); return; }
          const remaining = get().items.filter((i) => i.variantId !== variantId);
          if (remaining.length === 0) clearCart();
          else set({ items: remaining });
        } finally { set({ isLoading: false }); }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null, requestInsuranceInvoice: false }),
      getCheckoutUrl: () => get().checkoutUrl,

      setRequestInsuranceInvoice: async (v) => {
        set({ requestInsuranceInvoice: v });
        const { cartId } = get();
        if (!cartId) return;
        try {
          await storefrontApiRequest(CART_ATTRS, {
            cartId,
            attributes: [{ key: "Cranial Prosthesis Invoice Requested", value: v ? "Yes" : "No" }],
          });
          if (v) {
            await storefrontApiRequest(CART_NOTE, {
              cartId,
              note: "Customer requested a cranial prosthesis invoice (HCPCS A9282) for insurance/HSA reimbursement.",
            });
          }
        } catch (e) { console.error("attr update", e); }
      },

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;
        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) clearCart();
        } catch (e) { console.error("sync", e); }
        finally { set({ isSyncing: false }); }
      },
    }),
    {
      name: "europeanwigluxury-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        items: s.items, cartId: s.cartId, checkoutUrl: s.checkoutUrl,
        requestInsuranceInvoice: s.requestInsuranceInvoice,
      }),
    },
  ),
);
